import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import { Box, CircularProgress, Typography } from '@mui/material';
import React from 'react';
import { Link as RouterLink } from 'react-router-dom';

import { FORM_MAX_WIDTH } from './constants';
import { CreateServiceForm } from './CreateServiceForm';
import { InteractionsPanel } from './sections/InteractionsPanel';
import { useCreateService } from './useCreateService';
import { PageContainer } from '../../../components/atoms/PageContainer';
import { PLATFORM_NAME } from '../../../configs/common';
import { TabHandler } from '../../../handlers/TabHandler';
import { getBlockInteractionMessage } from '../../../utils/interactions';

const Breadcrumb: React.FC = () => (
  <Box
    sx={{
      px: 3,
      py: 1,
      fontSize: '13px',
      color: 'text.secondary',
      display: 'flex',
      gap: 0.75,
      alignItems: 'center',
    }}
  >
    <Typography
      component={RouterLink}
      to="/services"
      sx={{ color: 'v4.accent', textDecoration: 'none', fontSize: '13px' }}
    >
      Services
    </Typography>
    <span>/</span>
    <span>Create Service</span>
  </Box>
);

const PageHeader: React.FC = () => (
  <Box sx={{ px: 3, pb: 1.5, display: 'flex', alignItems: 'center', gap: 1.5 }}>
    <AddCircleOutlineIcon sx={{ color: 'v4.accent', fontSize: 24 }} />
    <Typography sx={{ fontWeight: 700, fontSize: '18px' }}>Create Service</Typography>
  </Box>
);

export const CreateService: React.FC = () => {
  TabHandler(`Create Service | ${PLATFORM_NAME}`);

  const model = useCreateService();

  if (!model.permissionsLoaded) {
    return (
      <PageContainer>
        <Box sx={{ p: 6, display: 'flex', justifyContent: 'center' }}>
          <CircularProgress />
        </Box>
      </PageContainer>
    );
  }

  if (!model.hasCreatePermission) {
    return (
      <PageContainer>
        <Box sx={{ p: 4, textAlign: 'center' }}>
          <Typography color="error">You do not have permission to create services.</Typography>
        </Box>
      </PageContainer>
    );
  }

  const blockedMessage = model.interactions.blocked
    ? getBlockInteractionMessage(model.interactions.forms)
    : undefined;

  return (
    <PageContainer>
      <Box sx={{ bgcolor: 'background.default', minHeight: '100vh' }}>
        <Breadcrumb />
        <PageHeader />
        <Box
          sx={{
            px: 3,
            pb: 4,
            display: 'flex',
            flexDirection: { xs: 'column', md: 'row' },
            alignItems: 'flex-start',
            gap: 3,
          }}
        >
          <Box sx={{ width: '100%', maxWidth: FORM_MAX_WIDTH, flexShrink: 0 }}>
            <CreateServiceForm model={model} />
          </Box>
          <Box sx={{ width: '100%', flex: 1, minWidth: 0 }}>
            <InteractionsPanel
              forms={model.interactions.forms}
              mandatoryCount={model.interactions.mandatoryCount}
              unfilledMandatoryCount={model.interactions.unfilledMandatoryCount}
              serviceId={model.interactions.serviceId}
              serviceProxy={model.serviceProxy}
              onChangeForm={model.interactions.updateForm}
              blockedMessage={blockedMessage}
            />
          </Box>
        </Box>
      </Box>
    </PageContainer>
  );
};
